import React, { FC } from 'react';
import { Typography, Portal, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { StyledLink } from '../Components';
import { addRank } from './BacklogModel';
import { Filter } from './BacklogFilter';
import { RankCell } from './RankCell';
import { FilterProps, selectBacklogFilter, setFilterModel } from './filterSlice';
import { useQuery } from '@apollo/client';
import LoadingSpinner from '../LoadingSpinner';
import { formatDateOnly } from '../Formatters';
import { GET_ALL } from './backlog.graphql';
import { ExcelExport } from './ExcelExport';
import { Criterion, selectCriterion } from './criterionSlice';
import { Box, useMediaQuery, useTheme } from '@mui/system';
import { BacklogItem, BacklogItemWithRank } from './types';
import { CategoryModel } from '../__generated__/graphql';
import { Stack } from '@mui/material';
import { StatusChip, TagsCell, getGuideStatusLabel } from '../guides/GuideStatusCells';
import { useAuth } from 'oidc-react';
import { StyledPaper } from '../theme';
import {
  DataGridPro,
  GridApiPro,
  GridFilterInputValueProps,
  GridFilterOperator,
  GridRenderCellParams,
  GridToolbarQuickFilter,
  getGridSingleSelectOperators,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid-pro/locales';

type TagKey = 'changes' | 'revision' | 'planlagt' | 'kartlegging' | 'notices';

const TAG_OPTIONS: { key: TagKey; label: string }[] = [
  { key: 'changes', label: 'Upubliserte endringer' },
  { key: 'revision', label: 'Under revisjon' },
  { key: 'planlagt', label: 'Planlagt' },
  { key: 'kartlegging', label: 'Kartlegging pågår' },
  { key: 'notices', label: 'Merknader' },
];

function getTagsForRow(guide: BacklogItemWithRank): TagKey[] {
  const tags: TagKey[] = [];
  const releases = guide.releases ?? [];
  const hasRelease = releases.length > 0;
  if (guide.hasChangesSinceLastPublish && hasRelease) tags.push('changes');
  if (guide.ongoingRevision) tags.push('revision');
  if (guide.isRevisionPlanned) tags.push('planlagt');
  if (guide.isRevisionAssesmentInProgress) tags.push('kartlegging');
  if ((guide.notices?.filter((n) => !n?.deletedAt).length ?? 0) > 0) tags.push('notices');
  return tags;
}

function TagFilterInput({ item, applyValue }: GridFilterInputValueProps) {
  const value: TagKey[] = Array.isArray(item.value) ? item.value : [];
  return (
    <FormControl variant="standard" fullWidth sx={{ minWidth: 180 }}>
      <InputLabel shrink>Tagger</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => {
          const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          applyValue({ ...item, value: next });
        }}
        renderValue={(selected) => (selected as TagKey[]).map((k) => TAG_OPTIONS.find((o) => o.key === k)?.label ?? k).join(', ')}>
        {TAG_OPTIONS.map((opt) => (
          <MenuItem key={opt.key} value={opt.key}>
            <Checkbox checked={value.includes(opt.key)} size="small" />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const tagAllOperator: GridFilterOperator<any, TagKey[]> = {
  label: 'inneholder alle',
  value: 'containsAll',
  getApplyFilterFn: (filterItem) => {
    const selected: TagKey[] = Array.isArray(filterItem.value) ? filterItem.value : [];
    if (selected.length === 0) return null;
    return (rowValue) => {
      const tags = rowValue ?? [];
      return selected.every((t) => tags.includes(t));
    };
  },
  InputComponent: TagFilterInput,
};

export function Overview() {
  const auth = useAuth();
  const profile = auth.userData?.profile;
  const filter: FilterProps = useSelector(selectBacklogFilter);
  const { ignoreCriteria }: Criterion = useSelector(selectCriterion);
  const apiRef = useGridApiRef();

  const { data, loading } = useQuery(GET_ALL, { fetchPolicy: 'cache-and-network' });

  const filteredItems = data?.guides && filterItems(data.guides, filter);
  const rankedItems = filteredItems && addRank(filteredItems, ignoreCriteria);

  return (
    <StyledPaper>
      <Typography variant="h5">Planlegging</Typography>
      {rankedItems && <ExcelExport apiRef={apiRef} />}
      <Filter />
      {loading && !data && <LoadingSpinner />}
      {rankedItems && <BacklogDataGrid items={rankedItems!} loading={loading} subjectId={profile?.sub} apiRef={apiRef} />}
    </StyledPaper>
  );
}

const BacklogDataGrid: FC<{
  items: BacklogItemWithRank[];
  loading: boolean;
  subjectId?: string;
  apiRef: React.MutableRefObject<GridApiPro>;
}> = ({ items, loading, subjectId, apiRef }) => {
  const theme = useTheme();
  const hasSpace = useMediaQuery(theme.breakpoints.up('lg'));
  const { filterModel } = useSelector(selectBacklogFilter);
  const dispatch = useDispatch();

  const categories = toDistictCategories(items);
  const myCategories = toDistictCategories(items.filter((x) => x!.owner?.subjectId === subjectId));

  const operator: GridFilterOperator<any, string> = {
    label: 'er mine',
    value: 'mine',
    getApplyFilterFn: (_filterItem, _column) => {
      return (value, _row, _column, _apiRef) => {
        return myCategories?.includes(value) ?? false;
      };
    },
  };

  return (
    <Stack spacing={2} marginTop={2}>
      <Box id="filter-panel" />
      <DataGridPro
        rows={items}
        loading={loading}
        apiRef={apiRef}
        disableRowSelectionOnClick
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        onFilterModelChange={(model) => dispatch(setFilterModel(model))}
        slots={{
          toolbar: CustomToolbar,
        }}
        density="compact"
        columns={[
          { field: 'rank', headerName: 'Score', type: 'number', width: 100, renderCell: (params) => <RankCell item={params.row} /> },
          {
            field: 'status',
            headerName: 'Status',
            type: 'singleSelect',
            valueOptions: [
              { value: 'Aktiv', label: 'Aktiv' },
              { value: 'Ny', label: 'Ny' },
              { value: 'Tilbaketrukket', label: 'Tilbaketrukket' },
            ],
            valueGetter: (_, row: BacklogItemWithRank) => getGuideStatusLabel(row),
            renderCell: ({ row: guide }) => <StatusChip guide={guide} />,
          },
          {
            field: 'docName',
            headerName: 'Nummer',
            renderCell: (params: GridRenderCellParams<any, string>) => (
              <StyledLink to={`/guide/${params.row.id}`}>{params.value ?? <span style={{ fontStyle: 'italic' }}>Uten navn</span>}</StyledLink>
            ),
          },
          {
            field: 'docTitle',
            headerName: 'Tittel',
            flex: 2,
          },
          {
            field: 'edition',
            headerName: 'Utgave',
            type: 'number',
            valueGetter: (_: string, row: BacklogItemWithRank) => {
              return row.planningScores?.edition?.value;
            },
            valueFormatter: (value: number) => {
              return value?.toString();
            },
          },
          {
            field: 'pageViews',
            headerName: 'Visninger',
            type: 'number',
            valueGetter: (_: string, row: BacklogItemWithRank) => {
              return row.planningScores?.pageViews?.value;
            },
            valueFormatter: (value: number) => {
              return value?.toLocaleString('no-NO');
            },
          },
          {
            field: 'placement',
            headerName: 'Rangering',
            type: 'number',
            valueGetter: (_: string, row: BacklogItemWithRank) => {
              const placement = row.planningScores?.pageViews?.placement;
              return placement?.total ? placement.index + 1 : undefined;
            },
            renderCell: (params: GridRenderCellParams<NonNullable<BacklogItem>, undefined>) => {
              const placement = params.row.planningScores?.pageViews?.placement;
              return placement?.total ? <span>{`${placement.index + 1} av ${placement.total}`}</span> : null;
            },
          },
          {
            field: 'mainCategory',
            headerName: 'Fagområde',
            type: 'singleSelect',
            valueOptions: categories.map((x) => {
              return { value: x, label: x };
            }),
            filterOperators: [...getGridSingleSelectOperators(), operator],
            flex: 1,
            valueGetter: (value: CategoryModel) => {
              if (!value) {
                return undefined;
              }
              return value.title;
            },
          },
          {
            field: 'lastPublishedAt',
            headerName: 'Sist endret',
            type: 'date',
            valueGetter: (value: string) => {
              if (!value) {
                return undefined;
              }
              return new Date(value);
            },
            valueFormatter: (value?: Date) => {
              return value ? formatDateOnly(value) : '';
            },
          },
          {
            field: 'tagger',
            headerName: 'Tagger',
            sortable: false,
            width: 140,
            valueGetter: (_, row: BacklogItemWithRank) => getTagsForRow(row),
            filterOperators: [tagAllOperator],
            renderCell: ({ row: guide }) => <TagsCell guide={guide} />,
          },
        ]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 50,
            },
          },
          sorting: {
            sortModel: [
              {
                field: 'rank',
                sort: 'desc',
              },
            ],
          },
          filter: {
            filterModel: filterModel,
          },
          columns: {
            columnVisibilityModel: {
              rank: false,
              edition: hasSpace,
              pageViews: hasSpace,
              tasksAndComments: hasSpace,
            },
          },
        }}
      />
    </Stack>
  );
};

function CustomToolbar(_props: any) {
  return (
    <React.Fragment>
      <Portal container={() => document.getElementById('filter-panel')!}>
        <GridToolbarQuickFilter sx={{ width: '100%' }} />
      </Portal>
    </React.Fragment>
  );
}

function filterItems(items: BacklogItem[], { showBvn, showBfs }: FilterProps) {
  const filteredGuides = items.filter((x) => x && (showBfs || x.docName?.startsWith('BVN')) && (showBvn || !x.docName?.startsWith('BVN')));
  return filteredGuides;
}

function toDistictCategories(items: BacklogItem[]): string[] {
  const set = new Set<string>();
  items.forEach((x) => {
    if (x?.mainCategory?.title) {
      set.add(x.mainCategory.title);
    }
  });
  return Array.from(set).sort();
}
